import { useState } from 'react'
import { truncateText } from '@opallabs/lambda'
import { Button, InputLabel, Modal, TextInput } from '@opallabs/lapidary'
import { type CalendarItem } from '../types'

/** The longest name the moment and content endpoints accept. */
const MAX_NAME_LENGTH = 100

/** Long enough to recognise an item, short enough to keep the title one line. */
const TITLE_LENGTH = 75

type DialogProps = Readonly<{
  /**
   * The item the dialog is about. `null` closes it — which is also what empties
   * it, because a dialog nobody is looking at should not hold on to a name.
   */
  item: CalendarItem | null
  onCancel: () => void
}>

const RenameForm: React.FC<{
  isSaving: boolean
  item: CalendarItem
  onCancel: () => void
  onRename: (name: string) => void
}> = ({ isSaving, item, onCancel, onRename }) => {
  const [name, setName] = useState(item.title)

  const isTooLong = name.length > MAX_NAME_LENGTH
  const canSave = name.trim().length > 0 && !isTooLong && !isSaving

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (canSave) onRename(name.trim())
      }}
    >
      <Modal.Title>Rename</Modal.Title>
      <Modal.Body>
        {/*
          The title says only what is happening, so the item being renamed is
          named here — which is also what the dialog is described by.
        */}
        <Modal.Description>
          Choose a new name for <q>{truncateText(item.title, TITLE_LENGTH)}</q>.
        </Modal.Description>
        <InputLabel inputId="calendar-rename" required text="Name" />
        <TextInput
          explicitError={
            isTooLong
              ? `Name cannot be longer than ${MAX_NAME_LENGTH} characters.`
              : ''
          }
          htmlId="calendar-rename"
          value={name}
          onChange={setName}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          buttonText="Cancel"
          name="cancel-rename"
          onClick={onCancel}
          variant="ghost"
        />
        <Button
          buttonText="Save"
          disabled={!canSave}
          name="save-rename"
          type="submit"
          variant="primary"
        />
      </Modal.Footer>
    </form>
  )
}
RenameForm.displayName = 'Calendar: RenameForm'

/**
 * Renames one item.
 *
 * The form is mounted with the dialog rather than kept alive underneath it, so
 * the field always opens on the name the item has now.
 */
export const RenameItemDialog: React.FC<
  DialogProps & { isSaving?: boolean; onRename: (name: string) => void }
> = ({ isSaving = false, item, onCancel, onRename }) => (
  <Modal.Root
    open={item !== null}
    onOpenChange={(open) => {
      if (!open) onCancel()
    }}
  >
    <Modal.Content aria-label="Rename">
      {item !== null && (
        <RenameForm
          isSaving={isSaving}
          item={item}
          onCancel={onCancel}
          onRename={onRename}
        />
      )}
    </Modal.Content>
  </Modal.Root>
)
RenameItemDialog.displayName = 'Calendar: RenameItemDialog'

/**
 * What deleting this item costs, said plainly.
 *
 * A moment takes its content with it, so a moment that holds any says so. There
 * is nothing to say about an empty one beyond that it does not come back.
 */
const deleteWarning = (item: CalendarItem): string => {
  if (item.kind === 'content') {
    return 'Deleting this content will remove it everywhere in Opal. You cannot undo this action.'
  }
  return item.childCount
    ? 'Deleting this moment will also delete any content within it. You cannot undo this action.'
    : 'You cannot undo this action.'
}

/** Asks before deleting, always. There is no undo behind it. */
export const DeleteItemDialog: React.FC<
  DialogProps & { isDeleting?: boolean; onConfirm: () => void }
> = ({ isDeleting = false, item, onCancel, onConfirm }) => (
  <Modal.Root
    open={item !== null}
    onOpenChange={(open) => {
      if (!open) onCancel()
    }}
  >
    <Modal.Content aria-label="Delete">
      {item !== null && (
        <>
          <Modal.Title>
            Delete <q>{truncateText(item.title, TITLE_LENGTH)}</q>?
          </Modal.Title>
          <Modal.Body>
            <Modal.Description>{deleteWarning(item)}</Modal.Description>
          </Modal.Body>
          <Modal.Footer>
            <Button
              buttonText="Cancel"
              name="cancel-delete"
              onClick={onCancel}
              variant="ghost"
            />
            <Button
              buttonText="Delete"
              disabled={isDeleting}
              name="confirm-delete"
              onClick={onConfirm}
              variant="primaryDestructive"
            />
          </Modal.Footer>
        </>
      )}
    </Modal.Content>
  </Modal.Root>
)
DeleteItemDialog.displayName = 'Calendar: DeleteItemDialog'
